// Pure, deterministic turning point detector for OmniCine timeline.
// - Input: timeline snapshot (clips, markers, ranges, durationSec)
// - Output: TurningPoint[] sorted by timeSec
// NOTE: Read-only, pure functions only. Keep lightweight.

export type TurningPoint = {
  id: string;
  timeSec: number;
  strength: number; // 0..1
  kind: "clip-boundary" | "marker-density" | "range-edge" | "mixed";
  meta?: Record<string, unknown>;
};

type TimelineSnapshot = {
  durationSec?: number;
  clips?: Array<{ id?: string; startSec?: number; endSec?: number }>;
  markers?: Array<{ id?: string; timeSec?: number }>;
  ranges?: Array<{ id?: string; startSec?: number; endSec?: number }>;
};

// simple stable id generator for derived points
function tid(x: number) {
  return `tp:${Math.round(x * 1000)}:${Math.random().toString(36).slice(2, 8)}`;
}

// compute turning points: small heuristic combining clip boundaries and marker density
export function getTurningPoints(snapshot: TimelineSnapshot): TurningPoint[] {
  const clips = Array.isArray(snapshot.clips) ? snapshot.clips : [];
  const markers = Array.isArray(snapshot.markers) ? snapshot.markers : [];
  const ranges = Array.isArray(snapshot.ranges) ? snapshot.ranges : [];

  const events: number[] = [];

  for (const c of clips) {
    const s = Number(c?.startSec ?? NaN);
    const e = Number(c?.endSec ?? NaN);
    if (Number.isFinite(s)) events.push(s);
    if (Number.isFinite(e)) events.push(e);
  }

  for (const m of markers) {
    const t = Number(m?.timeSec ?? NaN);
    if (Number.isFinite(t)) events.push(t);
  }

  for (const r of ranges) {
    const s = Number(r?.startSec ?? NaN);
    const e = Number(r?.endSec ?? NaN);
    if (Number.isFinite(s)) events.push(s);
    if (Number.isFinite(e)) events.push(e);
  }

  // sort and dedupe
  events.sort((a, b) => a - b);
  const uniq: number[] = [];
  for (const v of events) {
    if (uniq.length === 0 || Math.abs(uniq[uniq.length - 1] - v) > 1e-3) uniq.push(v);
  }

  // score each candidate by local marker/clip density (within 1s window)
  const out: TurningPoint[] = uniq.map((t) => {
    const window = 1.0; // seconds
    const count = events.filter((x) => Math.abs(x - t) <= window).length;
    const strength = Math.min(1, count / 6);
    const kind: TurningPoint['kind'] = count > 2 ? 'mixed' : 'clip-boundary';
    return { id: tid(t), timeSec: t, strength, kind };
  });

  // keep only significant peaks (strength > 0.1) and inside duration
  const duration = Number(snapshot.durationSec ?? Number.POSITIVE_INFINITY);
  const filtered = out.filter((p) => p.timeSec >= 0 && p.timeSec <= duration && p.strength > 0.05);

  // return up to 50 points
  return filtered.slice(0, 50);
}

export default getTurningPoints;
